import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { useI18n, formatCurrency } from '@/lib/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Sparkles, BookOpen, X } from 'lucide-react';
import { toast } from 'sonner';

export default function Recipes() {
  const { t, lang } = useI18n();
  const [recipes, setRecipes] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const [r, p] = await Promise.all([api.get('/recipes'), api.get('/products')]);
    setRecipes(r.data);
    setProducts(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const newRecipe = () => { setEditing(null); setOpen(true); };
  const editRecipe = async (r) => {
    const full = await api.get(`/recipes/${r.id}`);
    setEditing(full.data); setOpen(true);
  };

  return (
    <div className="space-y-4" data-testid="recipes-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold">{t('nav_recipes')}</h1>
        <Button onClick={newRecipe} data-testid="recipes-new-button"><Plus className="h-4 w-4 mr-1" /> {t('new_dish')}</Button>
      </div>
      {recipes.length === 0 ? (
        <Card><CardContent className="text-center py-12">
          <BookOpen className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto mb-3" />
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{lang === 'es' ? 'Crea tu primer escandallo.' : 'Crie seu primeiro escandallo.'}</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {recipes.map(r => (
            <Card key={r.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => editRecipe(r)} data-testid={`recipe-card-${r.id}`}>
              <CardContent className="p-4">
                <h3 className="font-heading font-semibold mb-2">{r.nombre}</h3>
                <div className="text-xs text-[hsl(var(--muted-foreground))] mb-3">{r.raciones} {t('portions')} • {(r.ingredientes||[]).length} {t('ingredients')}</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><div className="text-[hsl(var(--muted-foreground))]">{t('total_cost')}</div><div className="font-mono font-semibold">{formatCurrency(r.coste_total, lang)}</div></div>
                  <div><div className="text-[hsl(var(--muted-foreground))]">{t('cost_per_portion')}</div><div className="font-mono font-semibold">{formatCurrency(r.coste_por_racion, lang)}</div></div>
                  <div><div className="text-[hsl(var(--muted-foreground))]">{t('suggested_price')}</div><div className="font-mono font-semibold text-[hsl(var(--primary))]">{formatCurrency(r.pvp_sugerido, lang)}</div></div>
                  <div><div className="text-[hsl(var(--muted-foreground))]">Food cost</div><div className="font-mono">{r.food_cost_objetivo_pct}%</div></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <RecipeEditor open={open} onOpenChange={setOpen} editing={editing} products={products} t={t} lang={lang} onSaved={load} />
    </div>
  );
}

function RecipeEditor({ open, onOpenChange, editing, products, t, lang, onSaved }) {
  const [form, setForm] = useState({
    nombre: '', descripcion: '', categoria: '', raciones: 1,
    ingredientes: [], food_cost_objetivo_pct: 30, iva_pct: 10, alergenos: [], notas: '',
  });
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        nombre: editing.nombre || '',
        descripcion: editing.descripcion || '',
        categoria: editing.categoria || '',
        raciones: editing.raciones || 1,
        ingredientes: (editing.ingredientes || []).map(i => ({ product_id: i.product_id, cantidad: i.cantidad, perdida_pct: i.perdida_pct || 0 })),
        food_cost_objetivo_pct: editing.food_cost_objetivo_pct || 30,
        iva_pct: editing.iva_pct || 10,
        alergenos: editing.alergenos || [],
        notas: editing.notas || '',
      });
    } else {
      setForm({ nombre: '', descripcion: '', categoria: '', raciones: 1, ingredientes: [], food_cost_objetivo_pct: 30, iva_pct: 10, alergenos: [], notas: '' });
    }
    setAiResult(null);
  }, [editing, open]);

  const total = useMemo(() => {
    return form.ingredientes.reduce((acc, ing) => {
      const p = products.find(pp => pp.id === ing.product_id);
      if (!p) return acc;
      return acc + (p.coste_por_unidad || 0) * (ing.cantidad || 0) * (1 + (ing.perdida_pct || 0) / 100);
    }, 0);
  }, [form.ingredientes, products]);
  const perPortion = total / Math.max(form.raciones, 1);
  const suggested = perPortion / Math.max(form.food_cost_objetivo_pct / 100, 0.01);

  const aiPricing = async () => {
    setAiLoading(true);
    try {
      const ingArr = form.ingredientes.map(ing => {
        const p = products.find(pp => pp.id === ing.product_id) || {};
        return { nombre: p.nombre, cantidad: ing.cantidad, coste: (p.coste_por_unidad || 0) * (ing.cantidad || 0) };
      });
      const r = await api.post('/ai/recipe-pricing', {
        nombre: form.nombre, coste: Number(total.toFixed(2)),
        raciones: form.raciones, food_cost_objetivo_pct: form.food_cost_objetivo_pct,
        ingredientes: ingArr, lang,
      });
      setAiResult(r.data);
    } catch (e) { toast.error('AI error'); }
    finally { setAiLoading(false); }
  };

  const save = async () => {
    if (editing) await api.put(`/recipes/${editing.id}`, form);
    else await api.post('/recipes', form);
    toast.success('OK');
    onOpenChange(false); onSaved();
  };
  const remove = async () => {
    await api.delete(`/recipes/${editing.id}`);
    onOpenChange(false); onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="recipe-dialog">
        <DialogHeader><DialogTitle>{editing ? t('edit') : t('new_dish')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>{t('recipe_name')}</Label><Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} data-testid="recipe-name-input" /></div>
            <div><Label>{t('category')}</Label><Input value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value })} placeholder={lang === 'es' ? 'Entrante, Principal...' : 'Entrada, Principal...'} /></div>
            <div><Label>{t('portions')}</Label><Input type="number" min={1} value={form.raciones} onChange={e => setForm({ ...form, raciones: Number(e.target.value) })} data-testid="recipe-portions-input" /></div>
            <div><Label>{t('target_food_cost')}</Label><Input type="number" value={form.food_cost_objetivo_pct} onChange={e => setForm({ ...form, food_cost_objetivo_pct: Number(e.target.value) })} /></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>{t('ingredients')}</Label>
              <Button size="sm" variant="outline" onClick={() => setForm({ ...form, ingredientes: [...form.ingredientes, { product_id: products[0]?.id || '', cantidad: 0, perdida_pct: 0 }] })} data-testid="recipe-add-ingredient-button">
                <Plus className="h-3 w-3 mr-1" /> {t('add_ingredient')}
              </Button>
            </div>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--muted))]">
                  <tr>
                    <th className="text-left px-2 py-1.5 text-xs">{t('name')}</th>
                    <th className="text-right px-2 py-1.5 text-xs">{t('quantity')}</th>
                    <th className="text-right px-2 py-1.5 text-xs">{t('waste_pct')}</th>
                    <th className="text-right px-2 py-1.5 text-xs">{t('final_cost')}</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.ingredientes.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-6 text-[hsl(var(--muted-foreground))] text-sm">{lang === 'es' ? 'Sin ingredientes' : 'Sem ingredientes'}</td></tr>
                  )}
                  {form.ingredientes.map((ing, idx) => {
                    const p = products.find(pp => pp.id === ing.product_id);
                    const coste = (p?.coste_por_unidad || 0) * (ing.cantidad || 0) * (1 + (ing.perdida_pct || 0) / 100);
                    return (
                      <tr key={`ing-${idx}-${ing.product_id || 'new'}`} className="border-t">
                        <td className="px-2 py-1">
                          <Select value={ing.product_id} onValueChange={v => { const arr = [...form.ingredientes]; arr[idx].product_id = v; setForm({ ...form, ingredientes: arr }); }}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`recipe-ingredient-${idx}-product`}><SelectValue /></SelectTrigger>
                            <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre} ({p.unidad})</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-1 text-right">
                          <Input type="number" step="0.001" className="h-8 text-xs text-right" value={ing.cantidad} onChange={e => { const arr = [...form.ingredientes]; arr[idx].cantidad = Number(e.target.value); setForm({ ...form, ingredientes: arr }); }} data-testid={`recipe-ingredient-${idx}-qty`} />
                        </td>
                        <td className="px-2 py-1 text-right">
                          <Input type="number" step="0.1" className="h-8 text-xs text-right" value={ing.perdida_pct} onChange={e => { const arr = [...form.ingredientes]; arr[idx].perdida_pct = Number(e.target.value); setForm({ ...form, ingredientes: arr }); }} />
                        </td>
                        <td className="px-2 py-1 text-right font-mono">{formatCurrency(coste, lang)}</td>
                        <td className="px-1"><button onClick={() => setForm({ ...form, ingredientes: form.ingredientes.filter((_, i) => i !== idx) })} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]"><X className="h-3.5 w-3.5" /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-[hsl(var(--muted))]">
                  <tr>
                    <td colSpan={3} className="px-2 py-1.5 text-xs text-right font-semibold">{t('total_cost')}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-bold">{formatCurrency(total, lang)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <Card className="bg-[hsl(var(--accent))]">
            <CardContent className="p-3 grid grid-cols-3 gap-3">
              <div><div className="text-xs text-[hsl(var(--muted-foreground))]">{t('cost_per_portion')}</div><div className="font-mono font-bold">{formatCurrency(perPortion, lang)}</div></div>
              <div><div className="text-xs text-[hsl(var(--muted-foreground))]">{t('suggested_price')} (sin IVA)</div><div className="font-mono font-bold text-[hsl(var(--primary))]">{formatCurrency(suggested, lang)}</div></div>
              <div className="flex items-center justify-end">
                <Button size="sm" variant="outline" onClick={aiPricing} disabled={aiLoading || total === 0} data-testid="recipe-ai-pricing-button"><Sparkles className="h-3 w-3 mr-1" /> {t('ai_suggest_price')}</Button>
              </div>
            </CardContent>
          </Card>

          {aiResult && (
            <Card className="border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5">
              <CardContent className="p-3 text-sm space-y-1">
                <div className="flex items-center gap-2 font-semibold text-[hsl(var(--primary))]"><Sparkles className="h-4 w-4" /> IA</div>
                <div>PVP sin IVA: <strong>{formatCurrency(aiResult.pvp_sin_iva, lang)}</strong></div>
                <div>PVP con IVA 10%: <strong>{formatCurrency(aiResult.pvp_con_iva_10, lang)}</strong></div>
                <div>Margen: <strong>{aiResult.margen_pct}%</strong> • Food cost real: <strong>{aiResult.food_cost_real_pct}%</strong></div>
                {aiResult.comentario && <div className="italic text-[hsl(var(--muted-foreground))] mt-1">{aiResult.comentario}</div>}
              </CardContent>
            </Card>
          )}
        </div>
        <DialogFooter className="gap-2">
          {editing && <Button variant="destructive" onClick={remove} data-testid="recipe-delete-button"><Trash2 className="h-4 w-4" /></Button>}
          <Button onClick={save} disabled={!form.nombre} data-testid="recipe-save-button">{t('save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
